// Advanced Caching & Performance Layer
// Netflix-style multi-layer caching for instant loading

import { logger } from '../utils/logger';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Cache layers (fastest to slowest):
// L1: In-Memory Cache (React state) - <1ms
// L2: IndexedDB (browser) - <10ms
// L3: Service Worker Cache - <50ms
// L4: CDN Cache (Cloudflare) - <100ms
// L5: Supabase Database - <500ms

interface CacheEntry<T> {
  data: T;
  expiry: number;
  version: number;
}

interface CacheSchema extends DBSchema {
  cache: {
    key: string;
    value: CacheEntry<any> & { key: string };
    indexes: { 'by-expiry': number };
  };
  questions: {
    key: string;
    value: {
      key: string;
      testId: string;
      language: string;
      questions: any[];
      cachedAt: number;
    };
    indexes: { 'by-test': string; 'by-language': string };
  };
}

class AdvancedCacheService {
  // L1: In-memory cache
  private memoryCache = new Map<string, CacheEntry<any>>();

  // L2: IndexedDB database
  private db: IDBPDatabase<CacheSchema> | null = null;
  private dbPromise: Promise<IDBPDatabase<CacheSchema>> | null = null;
  private initRetryCount = 0;
  private readonly MAX_INIT_RETRIES = 3;

  // Cache configuration
  private readonly CACHE_TTL = {
    questions: 7 * 24 * 60 * 60 * 1000, // 7 days
    translations: 7 * 24 * 60 * 60 * 1000, // 7 days
    userProgress: 60 * 60 * 1000, // 1 hour
    recommendations: 30 * 60 * 1000, // 30 minutes
    default: 24 * 60 * 60 * 1000 // 24 hours
  };

  constructor() {
    this.initializeIndexedDB();
    this.initializeServiceWorkerCache();
    this.startCacheCleanup();
  }

  // Initialize IndexedDB
  private async initializeIndexedDB(): Promise<IDBPDatabase<CacheSchema> | null> {
    if (this.initRetryCount >= this.MAX_INIT_RETRIES) return null;
    if (!this.dbPromise) {
      try {
        // Store reference to 'this' for use in callbacks
        const self = this;
        this.dbPromise = openDB<CacheSchema>('theory-coach-cache', 1, {
          upgrade(db) {
            // Cache store
            if (!db.objectStoreNames.contains('cache')) {
              const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
              cacheStore.createIndex('by-expiry', 'expiry');
            }

            // Questions store
            if (!db.objectStoreNames.contains('questions')) {
              const questionsStore = db.createObjectStore('questions', { keyPath: 'key' });
              questionsStore.createIndex('by-test', 'testId');
              questionsStore.createIndex('by-language', 'language');
            }
          },
          blocked() {
            // Database is blocked by another connection
            logger.warn('IndexedDB is blocked by another connection');
          },
          terminated() {
            // Connection was terminated
            logger.warn('IndexedDB connection terminated');
            self.db = null;
            self.dbPromise = null;
          }
        });

        this.db = await this.dbPromise;
        this.initRetryCount = 0;
        logger.debug('IndexedDB cache initialized');
      } catch (error: any) {
        // Handle connection closure and deletion errors gracefully
        const errorName = String(error?.name || '').toLowerCase();
        const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
        const isConnectionError = 
          errorName === 'aborterror' ||
          errorName === 'invalidstateerror' ||
          errorMessage.includes('force close') ||
          errorMessage.includes('delete origin') ||
          errorMessage.includes('connection is closing') ||
          errorMessage.includes('deleted') ||
          errorMessage.includes('database deleted');
        
        if (isConnectionError) {
          this.initRetryCount += 1;
          if (this.initRetryCount >= this.MAX_INIT_RETRIES) {
            logger.warn('IndexedDB init failed after max retries — running without cache');
          } else {
            logger.debug('IndexedDB connection error (expected):', errorName);
          }
          this.db = null;
          this.dbPromise = null;
          return null;
        }
        
        logger.error('Failed to initialize IndexedDB:', error);
        this.db = null;
        this.dbPromise = null;
        return null;
      }
    }

    return this.dbPromise.catch(() => null);
  }

  // Initialize Service Worker cache
  private async initializeServiceWorkerCache(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const _registration = await navigator.serviceWorker.ready;
        logger.debug('Service Worker cache ready');
      } catch (error) {
        logger.error('Error initializing Service Worker cache:', error);
      }
    }
  }

  // Get from cache (tries all layers)
  async get<T>(key: string, _type: keyof typeof this.CACHE_TTL = 'default'): Promise<T | null> {
    // L1: Check in-memory cache
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && memoryEntry.expiry > Date.now()) {
      logger.debug(`Cache HIT (L1): ${key}`);
      return memoryEntry.data as T;
    }

    // L2: Check IndexedDB
    const indexedEntry = await this.getFromIndexedDB<T>(key);
    if (indexedEntry && indexedEntry.expiry > Date.now()) {
      // Promote to L1 cache
      this.memoryCache.set(key, indexedEntry);
      logger.debug(`Cache HIT (L2): ${key}`);
      return indexedEntry.data as T;
    }

    // L3: Check Service Worker cache (for static assets)
    if (key.startsWith('http')) {
      const swEntry = await this.getFromServiceWorker(key);
      if (swEntry) {
        logger.debug(`Cache HIT (L3): ${key}`);
        return swEntry as T;
      }
    }

    logger.debug(`Cache MISS: ${key}`);
    return null;
  }

  // Set in cache (all layers)
  async set<T>(key: string, data: T, type: keyof typeof this.CACHE_TTL = 'default'): Promise<void> {
    const ttl = this.CACHE_TTL[type] || this.CACHE_TTL.default;
    const entry: CacheEntry<T> = {
      data,
      expiry: Date.now() + ttl,
      version: 1
    };

    // L1: Set in-memory cache
    this.memoryCache.set(key, entry);

    // L2: Set in IndexedDB (async, don't block)
    this.setInIndexedDB(key, entry).catch(err => {
      logger.error('Error setting IndexedDB cache:', err);
    });

    // L3: Set in Service Worker cache (for static assets)
    if (key.startsWith('http') && 'caches' in window) {
      this.setInServiceWorker(key, data).catch(err => {
        logger.error('Error setting Service Worker cache:', err);
      });
    }
  }

  // Get from IndexedDB (L2)
  private async getFromIndexedDB<T>(key: string): Promise<CacheEntry<T> | null> {
    try {
      const db = await this.initializeIndexedDB();
      if (!db) return null;

      const tx = db.transaction('cache', 'readonly');
      
      // Add error handler to transaction to catch errors before they bubble up
      tx.onerror = (event: any) => {
        const error = event.target?.error;
        if (this.isConnectionError(error)) {
          // Suppress expected connection errors
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      };
      
      const store = tx.objectStore('cache');
      const entry = await store.get(key);
      
      return entry || null;
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return null;
      }
      logger.error('Error reading from IndexedDB:', error);
      return null;
    }
  }
  
  // Helper to check if error is a connection error
  private isConnectionError(error: any): boolean {
    if (!error) return false;
    const errorName = String(error?.name || '').toLowerCase();
    const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
    return (
      errorName === 'aborterror' ||
      errorName === 'invalidstateerror' ||
      errorName === 'domexception' ||
      errorMessage.includes('force close') ||
      errorMessage.includes('delete origin') ||
      errorMessage.includes('connection is closing') ||
      errorMessage.includes('connection is closing because of') ||
      errorMessage.includes('force close delete origin')
    );
  }

  // Set in IndexedDB (L2)
  private async setInIndexedDB<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    try {
      const db = await this.initializeIndexedDB();
      if (!db) return;

      const tx = db.transaction('cache', 'readwrite');
      
      // Add error handler to transaction to catch errors before they bubble up
      tx.onerror = (event: any) => {
        const error = event.target?.error;
        if (this.isConnectionError(error)) {
          // Suppress expected connection errors
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      };
      
      const store = tx.objectStore('cache');
      await store.put({ ...entry, key });
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return;
      }
      logger.error('Error writing to IndexedDB:', error);
    }
  }

  // Get from Service Worker cache (L3)
  private async getFromServiceWorker(url: string): Promise<any | null> {
    if (!('caches' in window)) return null;

    try {
      const cache = await caches.open('theory-coach-v1');
      const response = await cache.match(url);
      
      if (response) {
        return await response.json();
      }
    } catch (error) {
      logger.error('Error reading from Service Worker cache:', error);
    }

    return null;
  }

  // Set in Service Worker cache (L3)
  private async setInServiceWorker(url: string, data: any): Promise<void> {
    if (!('caches' in window)) return;

    try {
      const cache = await caches.open('theory-coach-v1');
      const response = new Response(JSON.stringify(data), {
        headers: { 'Content-Type': 'application/json' }
      });
      await cache.put(url, response);
    } catch (error) {
      logger.error('Error writing to Service Worker cache:', error);
    }
  }

  // Cache questions for a test
  async cacheQuestions(testId: string, language: string, questions: any[]): Promise<void> {
    const key = `questions_${testId}_${language}`;
    
    // Cache in all layers
    await this.set(key, questions, 'questions');

    // Also cache in IndexedDB questions store
    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('questions', 'readwrite');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('questions');
        await store.put({
          key,
          testId,
          language,
          questions,
          cachedAt: Date.now()
        });
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return;
      }
      logger.error('Error caching questions:', error);
    }
  }

  // Get cached questions for a test
  async getCachedQuestions(testId: string, language: string): Promise<any[] | null> {
    const key = `questions_${testId}_${language}`;
    
    // Try cache first
    const cached = await this.get<any[]>(key, 'questions');
    if (cached) return cached;

    // Try IndexedDB questions store
    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('questions', 'readonly');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('questions');
        const index = store.index('by-test');
        
        const questions = await index.getAll(testId);
        const match = questions.find((q: any) => q.language === language);
        
        if (match && match.questions) {
          // Promote to cache
          await this.set(key, match.questions, 'questions');
          return match.questions;
        }
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return null;
      }
      logger.error('Error getting cached questions:', error);
    }
    
    return null;
  }

  // Preload questions (cache warming)
  async preloadQuestions(testIds: string[], language: string): Promise<void> {
    // Use requestIdleCallback for non-blocking preload
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(async () => {
        for (const testId of testIds) {
          // Check if already cached
          const cached = await this.getCachedQuestions(testId, language);
          if (!cached) {
            // Preload in background (don't block)
            this.preloadSingleTest(testId, language).catch(err => {
              logger.error(`Error preloading ${testId}:`, err);
            });
          }
        }
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        testIds.forEach(testId => {
          this.preloadSingleTest(testId, language).catch(err => {
            logger.error(`Error preloading ${testId}:`, err);
          });
        });
      }, 1000);
    }
  }

  // Preload a single test
  private async preloadSingleTest(testId: string, language: string): Promise<void> {
    // This would fetch questions from your question loader
    // For now, we'll just log it
    logger.debug(`Preloading questions for ${testId} in ${language}`);
    
    // TODO: Integrate with questionLoader to actually fetch and cache
    // const questions = await questionLoader.loadQuestions(testId, language);
    // await this.cacheQuestions(testId, language, questions);
  }

  // Invalidate cache entry
  async invalidate(key: string): Promise<void> {
    // Remove from all layers
    this.memoryCache.delete(key);

    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('cache', 'readwrite');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('cache');
        await store.delete(key);
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return;
      }
      logger.error('Error invalidating cache:', error);
    }

    if ('caches' in window) {
      try {
        const cache = await caches.open('theory-coach-v1');
        await cache.delete(key);
      } catch (error) {
        logger.error('Error invalidating Service Worker cache:', error);
      }
    }
  }

  // Invalidate by pattern (e.g., all questions for a test)
  async invalidatePattern(pattern: string): Promise<void> {
    // Remove from memory cache
    const keysToDelete: string[] = [];
    this.memoryCache.forEach((value, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.memoryCache.delete(key));

    // Remove from IndexedDB
    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('cache', 'readwrite');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('cache');
        const allKeys = await store.getAllKeys();
        
        for (const key of allKeys) {
          if (key.includes(pattern)) {
            await store.delete(key);
          }
        }
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return;
      }
      logger.error('Error invalidating cache pattern:', error);
    }
  }

  // Start cache cleanup (remove expired entries)
  private startCacheCleanup(): void {
    // Clean up every hour
    setInterval(() => {
      this.cleanupExpiredEntries();
    }, 60 * 60 * 1000);
  }

  // Cleanup expired entries
  private async cleanupExpiredEntries(): Promise<void> {
    const now = Date.now();

    // Clean memory cache
    const keysToDelete: string[] = [];
    this.memoryCache.forEach((entry, key) => {
      if (entry.expiry <= now) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.memoryCache.delete(key));

    // Clean IndexedDB
    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('cache', 'readwrite');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('cache');
        const index = store.index('by-expiry');
        
        const expired = await index.getAll(IDBKeyRange.upperBound(now));
        for (const entry of expired) {
          await store.delete(entry.key);
        }
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return;
      }
      logger.error('Error cleaning up expired cache:', error);
    }
  }

  // Get cache statistics
  async getCacheStats(): Promise<{
    memorySize: number;
    indexedDBSize: number;
    hitRate: number;
  }> {
    let indexedDBSize = 0;
    
    try {
      const db = await this.initializeIndexedDB();
      if (db) {
        const tx = db.transaction('cache', 'readonly');
        
        // Add error handler to transaction to catch errors before they bubble up
        tx.onerror = (event: any) => {
          const error = event.target?.error;
          if (this.isConnectionError(error)) {
            // Suppress expected connection errors
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        };
        
        const store = tx.objectStore('cache');
        indexedDBSize = await store.count();
      }
    } catch (error: any) {
      // Suppress expected connection errors
      if (this.isConnectionError(error)) {
        return { memorySize: this.memoryCache.size, indexedDBSize: 0, hitRate: 0 };
      }
      logger.error('Error getting cache stats:', error);
    }

    return {
      memorySize: this.memoryCache.size,
      indexedDBSize,
      hitRate: 0 // TODO: Track hit/miss ratio
    };
  }
}

export const advancedCache = new AdvancedCacheService();

